import React, { useContext } from "react";
import {
  Box,
  Button,
  Stack,
  StackProps,
  chakra,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiMinus } from "react-icons/fi";
import { Link as GatsbyLink, graphql } from "gatsby";
import { ICON_SIZE, LessonProgress } from "./LessonProgress";
import { MultiCodeBlockContext } from "@apollo/chakra-helpers";

export type PromptFuncType = (
  callback: () => void
) => (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;

interface CourseNavProps extends StackProps {
  currentLesson: string;
  lessons: readonly Queries.LessonFragment[];
  prompt: PromptFuncType;
}

export function CourseNav({
  currentLesson,
  lessons,
  prompt,
  ...props
}: CourseNavProps) {
  const codeBlockContext = useContext(MultiCodeBlockContext);
  const bg = useColorModeValue("silver.400", "whiteAlpha.200");
  const textColor = useColorModeValue("blue.400", "blue.200");

  return (
    <Stack p="2" as="nav" {...props}>
      {lessons.map((lesson, index) => {
        if (!lesson.frontmatter) {
          return null;
        }

        const { title, conditionalLanguage } = lesson.frontmatter;
        const { showLessonNumbers } = lesson.course ?? {
          showLessonNumbers: false,
        };

        return (
          (!conditionalLanguage ||
            conditionalLanguage.toLowerCase() ===
              codeBlockContext.language?.toLowerCase()) && (
            <Button
              variant={currentLesson === lesson.slug ? "solid" : "ghost"}
              size="lg"
              bg={currentLesson === lesson.slug ? bg : "transparent"}
              color={currentLesson === lesson.slug ? textColor : "inherit"}
              px="3"
              justifyContent={"space-between"}
              key={lesson.id}
              as={GatsbyLink}
              to={`/${lesson.slug}`}
              title={title}
              onClick={prompt(() => window.gtag?.("event", "sidebar_navigate"))}
            >
              <chakra.span isTruncated>
                {showLessonNumbers ? `${index + 1}. ` : ""}
                {title}
              </chakra.span>
              {lesson.tasks.length ? (
                <LessonProgress tasks={lesson.tasks.map((task) => task.id)} />
              ) : (
                <Box color="gray.500" fontSize={ICON_SIZE} as={FiMinus} />
              )}
            </Button>
          )
        );
      })}
    </Stack>
  );
}

export const LessonFragment = graphql`
  fragment Lesson on Mdx {
    id
    slug
    tasks {
      id
    }
    frontmatter {
      title
      conditionalLanguage
    }
    course {
      showLessonNumbers
    }
  }
`;
